import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  getVehicleGroupStatus, cancelVehicleGroup, cancelOneVehicleInGroup,
  applyGroupConversionDecision,
} from '../../services/bookingService';
import { getGroupStatusBadge, TIER_CAPACITIES } from '../../services/multiVehicleService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function VehicleGroupStatus() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancellingVehicle, setCancellingVehicle] = useState(null);
  const [showCancelGroupModal, setShowCancelGroupModal] = useState(false);

  const reload = () => {
    setLoading(true);
    getVehicleGroupStatus(groupId)
      .then((res) => {
        setGroup(res.group);
        setVehicles(res.vehicles || []);
      })
      .catch((err) => toast.error(err.message || 'Could not load group'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // Live listener on parent doc — when group status changes, refresh
    const unsub = onSnapshot(doc(db, 'vehicleGroups', groupId), () => reload());
    return unsub;
  }, [groupId]);

  const handleCancelGroup = async () => {
    setCancelling(true);
    try {
      const result = await cancelVehicleGroup(groupId, 'rider_cancelled_group');
      toast.success(`${result.cancelledCount} vehicles cancelled. Total fees: $${result.totalFees.toFixed(2)}`);
      setShowCancelGroupModal(false);
      reload();
    } catch (err) {
      toast.error(err.message || 'Group cancellation failed');
    }
    setCancelling(false);
  };

  const handleCancelVehicle = async (bookingId) => {
    if (!window.confirm('Cancel this vehicle? Other vehicles in the group will continue. Standard cancellation fees may apply.')) return;
    setCancellingVehicle(bookingId);
    try {
      const result = await cancelOneVehicleInGroup(bookingId, 'rider_cancelled_one_vehicle');
      toast.success(result.cancellationFee > 0
        ? `Vehicle cancelled. $${result.cancellationFee.toFixed(2)} fee applied.`
        : 'Vehicle cancelled — no fee.');
      reload();
    } catch (err) {
      toast.error(err.message || 'Vehicle cancellation failed');
    }
    setCancellingVehicle(null);
  };

  const handleConversionDecision = async (decision) => {
    try {
      const result = await applyGroupConversionDecision(groupId, decision);
      if (decision === 'wait') {
        toast.success('Continuing to match drivers for 5 more minutes');
      } else if (decision === 'proceed') {
        toast.success(`Proceeding with ${vehicles.filter(v => v.status === 'confirmed').length} confirmed vehicles. ${result.cancelledPendingCount} pending vehicles cancelled.`);
      } else if (decision === 'cancel_all') {
        toast.success(`Group cancelled. ${result.standbyDriverCount} drivers compensated.`);
      }
      reload();
    } catch (err) {
      toast.error(err.message || 'Decision failed');
    }
  };

  if (loading) return <div className="p-10 text-center text-muted">Loading...</div>;
  if (!group) return <div className="p-10 text-center text-muted">Group not found</div>;

  const groupBadge = getGroupStatusBadge(group.status);
  const needsConversion = !!group.conversionDecisionRequiredAt && group.status === 'matching';
  const isPartial = group.status === 'partial';

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/rider/vehicle-groups')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← All vehicle groups</button>
      </div>

      <div className="flex justify-between items-start mb-4">
        <h1 className="font-serif m-0">
          {group.referenceNumber}
        </h1>
        <span className="px-3 py-1 rounded-full text-[0.78rem] font-bold"
          style={{ background: groupBadge.bg, color: groupBadge.color }}>{groupBadge.label}</span>
      </div>

      <div className="p-4 rounded-2xl border border-slate-200 mb-4 bg-white">
        <div className="text-[0.85rem] text-body leading-[1.7]">
          <div><strong>Pickup:</strong> {group.pickupAddress}</div>
          <div><strong>Destination:</strong> {group.destinationAddress}</div>
          <div>
            <strong>Pickup time:</strong>{' '}
            {group.scheduledPickupTime?._seconds
              ? new Date(group.scheduledPickupTime._seconds * 1000).toLocaleString('en-AU')
              : new Date(group.scheduledPickupTime).toLocaleString('en-AU')}
          </div>
          <div>
            <strong>Vehicles:</strong> {group.confirmedVehicleCount || 0} confirmed / {group.vehicleCount} total
          </div>
          {group.totalFare > 0 && (
            <div><strong>Total fare:</strong> ${group.totalFare.toFixed(2)}</div>
          )}
        </div>
      </div>

      {/* Conversion decision banner */}
      {needsConversion && (
        <div className="p-[18px] rounded-2xl mb-4"
          style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '2px solid #f59e0b' }}>
          <div className="font-extrabold mb-2" style={{ color: '#92400e' }}>
            ⏱ Only {group.confirmedVehicleCount} of {group.vehicleCount} drivers confirmed
          </div>
          <div className="text-[0.85rem] mb-3" style={{ color: '#78350f' }}>
            We've been searching for additional drivers. Choose how to proceed:
          </div>
          <div className="grid gap-2">
            <button onClick={() => handleConversionDecision('wait')} className="p-3 rounded-[10px] border-[1.5px] bg-white cursor-pointer font-semibold text-[0.9rem] text-left"
              style={{ borderColor: '#92400e', color: '#92400e' }}>⏳ Keep waiting — search for 5 more minutes</button>
            <button onClick={() => handleConversionDecision('proceed')} className="p-3 rounded-[10px] border-0 text-white cursor-pointer font-bold text-[0.9rem] text-left"
              style={{ background: '#059669' }}>✓ Proceed with {group.confirmedVehicleCount} confirmed vehicle{group.confirmedVehicleCount !== 1 ? 's' : ''} only</button>
            <button onClick={() => handleConversionDecision('cancel_all')} className="p-3 rounded-[10px] border-[1.5px] bg-white cursor-pointer font-semibold text-[0.9rem] text-left"
              style={{ borderColor: '#dc2626', color: '#dc2626' }}>✗ Cancel entire booking (no fee — platform-side failure)</button>
          </div>
        </div>
      )}

      {/* Partial-confirmed banner */}
      {isPartial && !needsConversion && (
        <div className="p-3.5 rounded-xl mb-4 text-[0.85rem]"
          style={{ background: '#fed7aa', border: '1px solid #fdba74', color: '#9a3412' }}>
          ⚠ Some vehicles are confirmed, others aren't yet matched. We're continuing to search.
        </div>
      )}

      {/* Cancel group button */}
      {['matching', 'partial', 'confirmed'].includes(group.status) && (
        <button onClick={() => setShowCancelGroupModal(true)} className="w-full p-3 rounded-xl border-[1.5px] bg-white cursor-pointer font-semibold mb-4"
          style={{ borderColor: '#ff6b6b', color: '#ff6b6b' }}>Cancel entire group</button>
      )}

      {showCancelGroupModal && (
        <div className="p-[18px] rounded-2xl bg-white mb-4" style={{ border: '1px solid #fecaca' }}>
          <h3 className="mt-0 mb-2 font-serif" style={{ color: '#dc2626' }}>Cancel all vehicles?</h3>
          <p className="text-body text-[0.85rem] mb-3">
            Standard cancellation rules apply per-vehicle. Total fees depend on how close each vehicle is to its scheduled pickup.
          </p>
          <div className="flex gap-2">
            <button onClick={() => setShowCancelGroupModal(false)} className="flex-1 p-3 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Keep group</button>
            <button onClick={handleCancelGroup} disabled={cancelling} className="flex-1 p-3 rounded-[10px] border-0 text-white cursor-pointer font-bold"
              style={{ background: '#ff6b6b' }}>{cancelling ? '...' : 'Confirm cancel'}</button>
          </div>
        </div>
      )}

      {/* Individual vehicles */}
      <h3 className="mt-6 mb-3 text-brand-dark">Vehicles</h3>
      <div className="grid gap-3">
        {vehicles.map((v) => {
          const statusLabel = {
            pending_pool: 'Searching for driver...',
            pending_targeted: 'Offered to driver',
            confirmed: 'Driver confirmed',
            en_route: 'Driver en route',
            arrived: 'Driver arrived',
            in_progress: 'In progress',
            completed: 'Completed',
            cancelled: 'Cancelled',
          }[v.status] || v.status;
          const statusColor = v.status === 'cancelled' ? '#dc2626' :
                              v.status === 'completed' ? '#059669' :
                              v.status === 'confirmed' || v.status === 'in_progress' ? '#2dd4a8' : '#5a6578';
          const isActive = ['confirmed', 'en_route', 'arrived', 'in_progress'].includes(v.status);
          const isCancellable = ['pending_pool', 'pending_targeted', 'confirmed'].includes(v.status);
          // Any non-terminal vehicle can open its trip page (modify / add stop / view).
          const isManageable = !['cancelled', 'completed'].includes(v.status);

          return (
            <div key={v.id} className="p-4 rounded-2xl bg-white border border-slate-200">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-bold text-brand-dark">
                    {v.passengerGroupName || `Vehicle ${v.vehicleIndex + 1}`}
                  </div>
                  <div className="text-[0.85rem] text-body mt-0.5">
                    {TIER_CAPACITIES[v.tier]?.label} · {v.passengerCount} pax · Ref: {v.referenceNumber}
                  </div>
                </div>
                <span className="text-[0.78rem] font-semibold" style={{ color: statusColor }}>{statusLabel}</span>
              </div>

              {/* Driver details once confirmed */}
              {isActive && v.driverName && (
                <div className="p-2.5 rounded-[10px] bg-slate-50 mt-2 text-[0.85rem] text-body">
                  <strong>Driver:</strong> {v.driverName}<br/>
                  {v.driverVehicleMake && <><strong>Vehicle:</strong> {v.driverVehicleColour} {v.driverVehicleMake} {v.driverVehicleModel} · <span className="font-mono rounded font-bold" style={{ background: '#fef9c3', padding: '1px 6px' }}>{v.driverPlate}</span><br/></>}
                  <strong>Trip PIN:</strong> <span className="font-mono text-[1rem] font-bold text-brand-dark">{v.tripPin}</span>
                </div>
              )}

              {/* Fare */}
              {v.fareBreakdown?.totalFare && (
                <div className="mt-2 text-[0.85rem] text-brand-dark">
                  <strong>Fare:</strong> ${v.fareBreakdown.totalFare.toFixed(2)}
                </div>
              )}

              {/* Standby compensation note */}
              {v.standbyCompensation > 0 && (
                <div className="mt-2 p-2 rounded-lg text-[0.78rem]" style={{ background: 'rgba(245,158,11,0.1)', color: '#92400e' }}>
                  Driver received ${v.standbyCompensation.toFixed(2)} standby compensation
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-1.5 mt-3">
                {isManageable && (
                  <button onClick={() => navigate(`/rider/trip/${v.id}`)} className="flex-1 p-2 rounded-lg border-[1.5px] bg-white cursor-pointer font-semibold text-[0.8rem]"
                    style={{ borderColor: '#2dd4a8', color: '#2dd4a8' }}>{isActive ? 'View trip' : 'Manage / Modify'}</button>
                )}
                {isCancellable && (
                  <button
                    onClick={() => handleCancelVehicle(v.id)}
                    disabled={cancellingVehicle === v.id}
                    className="flex-1 p-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold text-[0.8rem]"
                    style={{ color: '#ff6b6b' }}
                  >{cancellingVehicle === v.id ? '...' : 'Cancel this vehicle'}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    <BackButtonBottom to="/rider/vehicle-groups" label="All vehicle groups" />
    </div>
  );
}